import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from "react";
import HistoricalsMatrix from "@/components/HistoricalsMatrix";
import ReservesMatrix from "@/components/ReservesMatrix";
import LiquidityMatrix from "@/components/LiquidityMatrix";
import TeamPanel from "@/components/TeamPanel";
import ClubDocumentsPanel from "@/components/documents/ClubDocumentsPanel";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, API_BASE, formatApiError, fmtCurrency, tokenStore } from "@/lib/api";
import { useT } from "@/lib/i18n";
import AppLayout from "@/components/AppLayout";
import StepPlan from "@/components/StepPlan";
import MoreMenu from "@/components/MoreMenu";
import ImportHub from "@/components/ImportHub";
import DownloadFolderHint from "@/components/DownloadFolderHint";
import ClubTour, { isTourDisabled, setTourDisabled } from "@/components/ClubTour";
import ClubSubpostGrowthCard from "@/components/ClubSubpostGrowthCard";
import ClubBrandingPanel from "@/components/ClubBrandingPanel";
import { BtwSettingsCard } from "@/components/btw/BtwSettingsCard";
import BudgetMatrix from "@/components/BudgetMatrix";
import BudgetComparisonView from "@/components/BudgetComparisonView";
import ExistingLoansPanel from "@/components/ExistingLoansPanel";
import RollForwardDialog from "@/components/RollForwardDialog";
import AiDepExtractDialog from "@/components/AiDepExtractDialog";
import DuplicateCleanupDialog from "@/components/DuplicateCleanupDialog";
import { Plus, Download, Trash2, ArrowLeft, ArrowRight, BarChart3, Layers, ArrowUpRight, FileSpreadsheet, Pencil, Check, X, Copy, Lock, Unlock, AlertTriangle, Footprints, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { saveOrDownload, useDownloadFolderName } from "@/lib/downloadFolder";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

// Templates are served as .xlsx by the backend at /api/templates/*.xlsx

export default function ClubPage() {
  const t = useT();
  const { clubId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canAiImport = user && (user.role === "admin" || user.tier === "STANDARD" || user.tier === "PREMIUM");
  const [club, setClub] = useState(null);
  const isOwner = club && (club.shared_role == null); // shared_role null → owner or admin
  const canManageTeam = isOwner && ["PREMIUM", "TRIAL"].includes(user?.tier);
  // Documenten-tab: eigenaar ziet 'm altijd (upsell voor niet-PREMIUM); teamleden alleen als de eigenaar PREMIUM is.
  const showDocuments = Boolean(club) && (isOwner || club.documents_enabled);
  const [aiDepOpen, setAiDepOpen] = useState(false);
  const [dupCleanupOpen, setDupCleanupOpen] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [rollOpen, setRollOpen] = useState(false);
  const [historicals, setHistoricals] = useState([]);
  const [depreciations, setDepreciations] = useState([]);
  const [existingLoans, setExistingLoans] = useState([]);
  const [subposts, setSubposts] = useState([]);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourDisabled, setTourDisabledState] = useState(isTourDisabled());
  const startTour = () => { if (tourDisabled) { setTourDisabled(false); setTourDisabledState(false); } setTourOpen(true); };
  const tourSteps = useMemo(() => {
    const roleText = !club ? "" : club.shared_role == null
      ? t("Jij bent eigenaar van deze club: je kunt alles bekijken, wijzigen en exporteren.")
      : club.shared_role === "editor"
        ? t("Deze club is met je gedeeld. Als editor kun je cijfers en scenario's bekijken én wijzigen.")
        : t("Deze club is met je gedeeld. Als viewer kun je alles bekijken en exporteren, maar niet wijzigen.");
    return [
      { target: '[data-testid="club-title"]', title: t("Welkom bij {club}").replace("{club}", club?.name || ""), text: roleText },
      { target: '[data-testid="stepplan"], [data-testid="stepplan-toggle"]', title: t("Stappenplan"), text: t("Linksonder staat het stappenplan: zes stappen van downloadmap en BTW-situatie tot eerste scenario. Klik op een stap om ernaartoe te gaan; met 'Verberg' blijft alleen het icoon over.") },
      { target: '[data-testid="tab-historicals"]', title: t("Voorgaande jaren"), text: t("De jaarrekeningen per boekjaar, met subposten voor inkomsten en kosten. Dit is de basis waarop elk scenario rekent. Begin met de blauwe AI-import van je jaarverslag (PDF); Excel of handmatig invullen kan ook.") },
      { target: '[data-testid="tab-depreciations"]', title: t("Afschrijvingsstaat"), text: t("Bestaande investeringen en hun jaarlijkse afschrijving; ze lopen automatisch mee in de scenario's.") },
      { target: '[data-testid="tab-existing-loans"]', title: t("Bestaande leningen"), text: t("Lopende leningen met rente en aflossing. Elk scenario rekent ze mee in de kasstroom en de schulddekkingsgraad (DSCR).") },
      { target: '[data-testid="tab-scenarios"]', title: t("Scenario's"), text: t("De meerjarenprojecties (bijv. Basis / Beste / Slechtste). Open een scenario voor het rapport, de grafieken, de risicosimulatie en de exports naar Excel, CSV en PDF.") },
      { target: '[data-testid="tab-comparison"]', title: t("Vergelijking"), text: t("Zet scenario's naast elkaar en vergelijk ze met de begroting.") },
      { target: '[data-testid="tab-team"]', title: t("Team"), text: t("Nodig teamleden uit als viewer of editor; zij zien deze club op hun eigen dashboard.") },
      { target: '[data-testid="club-more-menu"]', title: t("Meer acties"), text: t("Club kopiëren, vergrendelen en scenario's doorrollen naar een nieuw boekjaar vind je onder 'Meer'. Deze rondleiding start je opnieuw met de knop 'Rondleiding' ernaast.") },
      { target: '[data-testid="download-folder-btn"]', title: t("Downloadmap"), text: t("Kies één map op deze computer — bijvoorbeeld de gedeelde OneDrive-map van de club — waar alle exports van deze club terechtkomen en waar je importbestanden klaarzet.") },
      { target: '[data-testid^="help-btn-"]', title: t("Hulp"), text: t("Het ?-knopje opent de handleiding bij het juiste hoofdstuk. Rechtsonder zit de hulpassistent voor vragen over de app én over dit scenario.") },
    ];
  }, [club, t]);
  // Deep-link support: `?tab=budget` (of andere key) opent direct het juiste
  // tabblad. Handig zodat de baseline-link vanaf de Vergelijkings-pagina
  // direct in de Begroting-tab landt. Wijzigingen synchroniseren de URL zodat
  // een gebruiker de tab kan bookmarken / delen.
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ["historicals", "depreciations", "existing-loans", "scenarios", "budget", "comparison", "btw", "branding", "team", "documents"];
  const initialTab = (() => {
    const q = searchParams.get("tab");
    return q && VALID_TABS.includes(q) ? q : "historicals";
  })();
  const [tab, setTabState] = useState(initialTab);
  const setTab = (k) => {
    setTabState(k);
    // Update de URL zonder een history-entry per klik (replace).
    const next = new URLSearchParams(searchParams);
    if (k === "historicals") next.delete("tab"); else next.set("tab", k);
    setSearchParams(next, { replace: true });
  };
  // Reageer op externe URL-verandering (bv. deep-link vanaf compare-pagina).
  useEffect(() => {
    const q = searchParams.get("tab");
    const nextTab = q && VALID_TABS.includes(q) ? q : "historicals";
    if (nextTab !== tab) setTabState(nextTab);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [dupTarget, setDupTarget] = useState(null); // scenario being duplicated
  const [dupName, setDupName] = useState("");
  // Depreciations UX: hide fully-depreciated assets (book value = €0) from the
  // list once there are 3+ of them. State is persisted per club via localStorage
  // so the user's preference sticks between visits. Data is NEVER filtered on
  // import — only visually hidden — so nothing is ever lost.
  const zeroBookAssets = useMemo(
    () => (depreciations || []).filter((d) => Number(d.current_book_value || 0) <= 0),
    [depreciations]
  );
  const [hideZeroBook, setHideZeroBook] = useState(false);
  useEffect(() => {
    if (!clubId) return;
    const stored = localStorage.getItem(`clubfin:dep-hide-zero:${clubId}`);
    if (stored !== null) {
      setHideZeroBook(stored === "1");
    } else {
      // First-visit default: hide when it's noisy (3+ zero-book assets).
      setHideZeroBook(zeroBookAssets.length >= 3);
    }
  }, [clubId, zeroBookAssets.length]);
  const toggleHideZero = () => {
    const next = !hideZeroBook;
    setHideZeroBook(next);
    if (clubId) localStorage.setItem(`clubfin:dep-hide-zero:${clubId}`, next ? "1" : "0");
  };
  const depreciationsFiltered = useMemo(
    () => hideZeroBook
      ? (depreciations || []).filter((d) => Number(d.current_book_value || 0) > 0)
      : (depreciations || []),
    [depreciations, hideZeroBook]
  );

  const load = useCallback(async () => {
    try {
      const [c, s, h, d, sp, b, el] = await Promise.all([
        api.get(`/clubs/${clubId}`),
        api.get(`/clubs/${clubId}/scenarios`),
        api.get(`/clubs/${clubId}/historicals`),
        api.get(`/clubs/${clubId}/depreciations`),
        api.get(`/clubs/${clubId}/subposts`),
        api.get(`/clubs/${clubId}/budgets`),
        api.get(`/clubs/${clubId}/existing-loans`),
      ]);
      setClub(c.data); setScenarios(s.data); setHistoricals(h.data);
      setDepreciations(d.data); setSubposts(sp.data); setBudgets(b.data);
      setExistingLoans(el.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [clubId]);
  useEffect(() => { load(); }, [load]);

  const uploadCsv = async (endpoint, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/clubs/${clubId}/import/${endpoint}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.inserted} ${t("regels geïmporteerd") || "rows imported"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteScenario = async (id) => {
    if (!window.confirm(t("Dit scenario verwijderen?"))) return;
    await api.delete(`/scenarios/${id}`);
    load();
  };

  const duplicateClub = async () => {
    const suggested = `${club?.name || "Club"} (kopie)`;
    const name = window.prompt(t("Naam voor de kopie:"), suggested);
    if (name === null) return;
    try {
      const { data } = await api.post(`/clubs/${clubId}/duplicate`, { name: name.trim() || suggested });
      toast.success(t("Club gekopieerd — {n} scenario('s), {h} boekjaren").replace("{n}", data.copied?.scenarios || 0).replace("{h}", data.copied?.historicals || 0));
      nav(`/clubs/${data.club.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleClubLock = async () => {
    const willLock = !club?.is_locked;
    if (willLock && !window.confirm(t("Deze club vergrendelen? Er kunnen geen wijzigingen meer worden opgeslagen tot je ontgrendelt."))) return;
    try {
      const { data } = await api.put(`/clubs/${clubId}/lock`, { locked: willLock });
      setClub((prev) => ({ ...(prev || {}), is_locked: data.is_locked }));
      toast.success(data.is_locked ? t("Club vergrendeld") : t("Club ontgrendeld"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openDuplicate = (scenario) => {
    setDupTarget(scenario);
    setDupName(`${scenario.name} (kopie)`);
  };

  const confirmDuplicate = async () => {
    if (!dupTarget) return;
    const name = dupName.trim();
    if (!name) { toast.error(t("Naam mag niet leeg zijn")); return; }
    try {
      const { data } = await api.post(`/scenarios/${dupTarget.id}/duplicate`, { name });
      toast.success(t("Scenario gekopieerd"));
      setDupTarget(null);
      load();
      nav(`/clubs/${clubId}/scenarios/${data.id}/edit`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Memoize column definitions so DataTable children keep stable prop identity between renders.
  const histColumns = useMemo(() => [["year", t("Jaar")], ["revenue", t("Omzet")], ["operating_costs", t("Bedr. kosten")], ["depreciation", t("Afschrijving")], ["other_income", t("Overige ink.")], ["balance", t("Saldo")], ["cash_bank_balance", t("Kas + bank 31/12")]], [t]);
  // Duplicate detector for the "Duplicaten opruimen" button — same normalisation as AI-import.
  const depDuplicateCount = useMemo(() => {
    const seen = new Map();
    for (const d of depreciations) {
      const key = (d.asset_name || "")
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "")
        .trim();
      if (!key) continue;
      seen.set(key, (seen.get(key) || 0) + 1);
    }
    let n = 0;
    for (const c of seen.values()) if (c > 1) n += c - 1;
    return n;
  }, [depreciations]);
  const depColumns = useMemo(() => [
    ["asset_name", t("Actief")],
    ["acquired_year", t("Verkregen")],
    ["initial_value", t("Initieel")],
    ["useful_life_years", t("Levensduur")],
    ["current_book_value", t("Boekwaarde")],
    ["residual_value", t("Restwaarde")],
    ["start_date", t("Startdatum")],
    ["is_active", t("Actief?")],
  ], [t]);
  const currentYear = new Date().getFullYear();

  // Field specs used by the inline "Nieuwe rij" quick-add form.
  const histFields = useMemo(() => [
    { key: "year", label: t("Jaar"), type: "int", default: currentYear - 1 },
    { key: "revenue", label: t("Omzet (€)"), type: "num", default: 0 },
    { key: "operating_costs", label: t("Bedr. kosten excl. afschr. (€)"), type: "num", default: 0 },
    { key: "depreciation", label: t("Afschrijving (€)"), type: "num", default: 0 },
    { key: "other_income", label: t("Overige ink. (€)"), type: "num", default: 0 },
    { key: "balance", label: t("Saldo (€)"), type: "num", default: 0 },
    { key: "cash_bank_balance", label: t("Kas + bank 31/12 (€)"), type: "num", default: 0 },
  ], [t, currentYear]);
  const depFields = useMemo(() => [
    { key: "asset_name", label: t("Naam actief"), type: "text", default: "" },
    { key: "acquired_year", label: t("Verkregen in jaar"), type: "int", default: currentYear - 5 },
    { key: "initial_value", label: t("Initiële waarde (€)"), type: "num", default: 0 },
    { key: "useful_life_years", label: t("Levensduur (jaren)"), type: "int", default: 10 },
    { key: "current_book_value", label: t("Huidige boekwaarde (€)"), type: "num", default: 0 },
    {
      key: "residual_value",
      label: t("Restwaarde (optioneel, €)"),
      type: "num",
      default: 0,
    },
    {
      key: "start_date",
      label: t("Startdatum (optioneel)"),
      type: "date",
      default: "",
    },
    {
      key: "is_active",
      label: t("Actief afschrijven?"),
      type: "bool",
      default: true,
      checkboxLabel: t("Uitzetten voor buitengebruik / boekwaarde op 0"),
    },
  ], [t, currentYear]);

  const addRow = async (endpoint, payload) => {
    try {
      await api.post(`/clubs/${clubId}/${endpoint}`, payload);
      toast.success(t("Rij toegevoegd"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateRow = async (endpoint, rowId, payload) => {
    try {
      await api.put(`/${endpoint}/${rowId}`, payload);
      toast.success(t("Rij bijgewerkt"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!club) return <AppLayout><div className="stat-label">{t("Laden…")}</div></AppLayout>;

  return (
    <AppLayout>
      <Dialog open={!!dupTarget} onOpenChange={(open) => { if (!open) setDupTarget(null); }}>
        <DialogContent data-testid="duplicate-scenario-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2">
              <Copy className="w-5 h-5 text-primary" /> {t("Scenario kopiëren")}
            </DialogTitle>
            <DialogDescription>
              {t("Geef de kopie een naam. Alle instellingen, investeringen en groeivoeten worden overgenomen.")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="stat-label">{t("Nieuwe scenarionaam")}</label>
            <input
              autoFocus
              value={dupName}
              onChange={(e) => setDupName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); confirmDuplicate(); }
                else if (e.key === "Escape") { e.preventDefault(); setDupTarget(null); }
              }}
              data-testid="duplicate-scenario-name-input"
              className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <DialogFooter>
            <button onClick={() => setDupTarget(null)}
              className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold text-sm">
              {t("Annuleren")}
            </button>
            <button onClick={confirmDuplicate} data-testid="duplicate-scenario-confirm"
              className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm">
              {t("Kopiëren")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => nav("/")} data-testid="back-btn" className="text-sm inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> {t("Alle clubs")}
        </button>
      </div>

      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="stat-label flex items-center gap-2">
            {club.sport || "Sport"} · {club.location || "—"}
            {club.is_locked && (
              <span className="inline-flex items-center gap-1 bg-destructive text-destructive-foreground px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest" data-testid="club-locked-badge">
                <Lock className="w-3 h-3" /> {t("Vergrendeld")}
              </span>
            )}
          </div>
          <h1 className="font-display font-black text-5xl md:text-6xl uppercase tracking-tighter mt-1" data-testid="club-title">{club.name}</h1>
          {club.description && <p className="text-muted-foreground mt-2 max-w-2xl">{club.description}</p>}
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <button type="button" onClick={startTour} data-testid="tour-start-btn" title={t("Korte rondleiding door deze clubpagina")}
            className="btn-brutal bg-background text-foreground px-4 py-3 font-display uppercase font-black inline-flex items-center gap-2">
            <Footprints className="w-5 h-5" /> {t("Rondleiding")}
          </button>
          <MoreMenu testid="club-more-menu" items={[
            { label: t("Kopiëren"), icon: <Copy className="w-4 h-4" />, onClick: duplicateClub, testid: "club-duplicate-btn", hint: t("Volledige kopie van deze club") },
            { label: club.is_locked ? t("Ontgrendelen") : t("Vergrendelen"), icon: club.is_locked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />, onClick: toggleClubLock, testid: "club-lock-toggle", danger: !club.is_locked },
            { label: t("Rol scenario's door"), icon: <ArrowRight className="w-4 h-4" />, onClick: () => setRollOpen(true), disabled: scenarios.length === 0 || club.is_locked, testid: "roll-forward-btn", hint: t("Nieuw boekjaar") },
          ]} />
          <Link to={`/clubs/${clubId}/compare`} data-testid="compare-btn"
            className="btn-brutal bg-accent text-accent-foreground px-4 py-3 font-display uppercase font-black inline-flex items-center gap-2">
            <Layers className="w-5 h-5" /> {t("Vergelijken")}
          </Link>
          <Link to={`/clubs/${clubId}/scenarios/new`} data-testid="new-scenario-btn"
            className={`btn-brutal bg-primary text-primary-foreground px-4 py-3 font-display uppercase font-black inline-flex items-center gap-2 ${club.is_locked ? "opacity-40 pointer-events-none" : ""}`}
            onClick={(e) => { if (club.is_locked) { e.preventDefault(); toast.error(t("Club is vergrendeld — ontgrendel eerst.")); } }}>
            <Plus className="w-5 h-5" /> {t("Nieuw scenario")}
          </Link>
        </div>
      </div>

      <DownloadFolderHint clubId={clubId} shared={!isOwner} />
      <ClubTour steps={tourSteps} open={tourOpen} onClose={() => setTourOpen(false)} onDisable={() => { setTourDisabled(true); setTourDisabledState(true); }} />

      <StepPlan clubId={clubId} data={{ club, historicals, depreciations, existingLoans, scenarios }} />

      <div className="grid md:grid-cols-3 border-2 border-foreground mb-6 bg-card heavy-shadow divide-y-2 md:divide-y-0 md:divide-x-2 divide-foreground" data-testid="club-tab-groups">
        {[
          { key: "cijfers", label: t("Cijfers"), tabs: [["historicals", t("Voorgaande jaren")], ["depreciations", t("Afschrijvingsstaat")], ["existing-loans", t("Bestaande leningen")]] },
          { key: "plannen", label: t("Plannen"), tabs: [["scenarios", t("Scenario's")], ["comparison", t("Vergelijking")], ["budget", t("Begroting")]] },
          { key: "club", label: t("Club"), tabs: [
            ["btw", t("BTW")],
            ...(isOwner ? [["branding", t("Huisstijl")]] : []),
            ...(canManageTeam ? [["team", t("Team")]] : []),
            ...(showDocuments ? [["documents", t("Documenten")]] : []),
          ] },
        ].filter((g) => g.tabs.length > 0).map((g) => (
          <div key={g.key} className="flex flex-col" data-testid={`tab-group-${g.key}`}>
            <div className={`px-4 py-2 font-display uppercase font-black text-base tracking-widest border-b-2 border-foreground ${g.tabs.some(([k]) => k === tab) ? "bg-foreground text-background" : "bg-muted text-muted-foreground"}`}>{g.label}</div>
            {g.tabs.map(([k, l]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                data-testid={`tab-${k}`}
                className={`text-left px-4 py-2.5 font-display uppercase font-bold tracking-wide text-base lg:text-lg border-l-8 transition-colors ${
                  tab === k ? "border-primary bg-primary/10 text-foreground" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >{l}</button>
            ))}
          </div>
        ))}
      </div>

      {tab === "scenarios" && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <ClubSubpostGrowthCard t={t} clubId={clubId} club={club} historicals={historicals} canEdit={club.shared_role !== "viewer" && !club.is_locked} onSaved={(c) => setClub((prev) => ({ ...(prev || {}), default_subpost_growth: c.default_subpost_growth }))} />
          {scenarios.length === 0 && (
            <div className="col-span-full border-2 border-dashed border-foreground/40 p-10 text-center">
              <BarChart3 className="w-10 h-10 mx-auto text-muted-foreground" />
              <div className="font-display uppercase font-bold text-xl mt-2">{t("Nog geen scenario's")}</div>
              <p className="text-muted-foreground">{t("Maak scenario's zoals Beste / Basis / Slechtste om uitkomsten te vergelijken.")}</p>
            </div>
          )}
          {scenarios.map((s) => (
            <div key={s.id} className="bg-card border-2 border-foreground p-6 heavy-shadow">
              <div className="flex items-start justify-between">
                <div>
                  <div className={`stat-label ${s.kind === 'best' ? 'text-positive' : s.kind === 'worst' ? 'text-destructive' : 'text-primary'}`}>{s.kind?.toUpperCase()}</div>
                  <div className="font-display font-black text-2xl uppercase tracking-tight mt-1">{s.name}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openDuplicate(s)} data-testid={`duplicate-scenario-${s.id}`}
                    title={t("Kopiëren")}
                    className="opacity-40 hover:opacity-100 hover:text-primary"><Copy className="w-4 h-4" /></button>
                  <button onClick={() => deleteScenario(s.id)} data-testid={`delete-scenario-${s.id}`}
                    title={t("Verwijderen")}
                    className="opacity-40 hover:opacity-100 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div>
                  <div className="stat-label">{t("Horizon")}</div>
                  <div className="font-mono-num font-bold text-xl">{s.horizon_years} {t("jr")}</div>
                </div>
                <div>
                  <div className="stat-label">{t("Startjaar")}</div>
                  <div className="font-mono-num font-bold text-xl">{s.start_year || "—"}</div>
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-border flex justify-between">
                <Link to={`/clubs/${clubId}/scenarios/${s.id}`} data-testid={`open-scenario-${s.id}`}
                  className="btn-brutal bg-primary text-primary-foreground px-3 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-1">
                  {t("Bekijken")} <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to={`/clubs/${clubId}/scenarios/${s.id}/edit`} data-testid={`edit-scenario-${s.id}`}
                  className="btn-brutal bg-background text-foreground px-3 py-2 font-display uppercase font-bold text-sm">{t("Bewerken")}</Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "team" && canManageTeam && (
        <TeamPanel clubId={clubId} currentUserTier={user?.tier} />
      )}

      {tab === "documents" && showDocuments && (
        <ClubDocumentsPanel clubId={clubId} userId={user?.id} onHistoricalsChanged={load} />
      )}

      {tab === "budget" && (
        <BudgetMatrix
          clubId={clubId}
          club={club}
          historicals={historicals}
          subposts={subposts}
          onSubpostsChange={load}
          clubLocked={Boolean(club?.is_locked)}
          userTier={user?.tier}
        />
      )}

      {tab === "existing-loans" && (
        <ExistingLoansPanel
          clubId={clubId}
          locked={Boolean(club?.is_locked)}
        />
      )}

      {tab === "comparison" && (
        <BudgetComparisonView clubId={clubId} club={club} userTier={user?.tier} canEdit={club.shared_role !== "viewer" && !club.is_locked} onOpenBudget={() => setTab("budget")} />
      )}

      {tab === "branding" && isOwner && (
        <ClubBrandingPanel club={club} onUpdated={(updated) => setClub(updated)} />
      )}

      {tab === "btw" && (
        <div className="bg-card border-2 border-foreground p-6 heavy-shadow">
          <BtwSettingsCard t={t} clubId={clubId} club={club} subposts={subposts}
            canEdit={club.shared_role !== "viewer" && !club.is_locked}
            onClubSaved={(c) => setClub((prev) => ({ ...(prev || {}), btw_regime: c.btw_regime, btw_default_rate: c.btw_default_rate, btw_pro_rata: c.btw_pro_rata, btw_default_aftrek: c.btw_default_aftrek, btw_aangiftetijdvak: c.btw_aangiftetijdvak, btw_confirmed: c.btw_confirmed }))}
            onSubpostsChanged={load} />
        </div>
      )}

      {tab === "historicals" && (
        <>
          <HistoricalsMatrix
            t={t}
            clubId={clubId}
            club={club}
            historicals={historicals}
            subposts={subposts}
            onChange={load}
            locked={Boolean(club?.historicals_locked || club?.is_locked)}
            clubLocked={Boolean(club?.is_locked)}
            canAiImport={canAiImport}
            userTier={user?.tier}
            onToggleLock={async () => {
              const nextLocked = !club?.historicals_locked;
              try {
                await api.put(`/clubs/${clubId}/historicals-lock`, { locked: nextLocked });
                setClub((c) => c ? { ...c, historicals_locked: nextLocked } : c);
                toast.success(nextLocked ? t("Vergrendeld") : t("Ontgrendeld"));
              } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail));
              }
            }}
          />
          <LiquidityMatrix
            t={t}
            clubId={clubId}
            historicals={historicals}
            locked={Boolean(club?.historicals_locked || club?.is_locked)}
            onChange={load}
          />
          <ReservesMatrix
            t={t}
            clubId={clubId}
            historicals={historicals}
            locked={Boolean(club?.historicals_locked || club?.is_locked)}
            onChanged={load}
          />
        </>
      )}
      {tab === "depreciations" && (
        <DataTable t={t}
          title={t("Afschrijvingsstaat")}
          columns={depColumns}
          rows={depreciationsFiltered}
          onDelete={async (r) => { await api.delete(`/depreciations/${r.id}`); load(); }}
          upload={(f) => uploadCsv("depreciations", f)}
          templateEndpoint="/templates/depreciations.xlsx"
          exportEndpoint={`/clubs/${clubId}/export/depreciations.xlsx`}
          addFields={depFields}
          onAdd={(payload) => addRow("depreciations", payload)}
          onUpdate={(rowId, payload) => updateRow("depreciations", rowId, payload)}
          testid="dep"
          clubId={clubId}
          locked={Boolean(club?.historicals_locked || club?.is_locked)}
          rowClassName={(r) => Number(r.current_book_value || 0) <= 0 ? "opacity-60 italic" : ""}
          aiImport={{
            label: t("AI-import afschrijvingsstaat"),
            description: t("Upload een Twinfield-export of PDF activaregister — de AI leest de activa, aanschafwaarden en levensduur en vult de staat voor je in."),
            badge: !canAiImport ? (user?.tier === "TRIAL" ? t("Niet in proef") : "PRO") : null,
            muted: !canAiImport,
            onClick: () => {
              if (!canAiImport) {
                toast.error(user?.tier === "TRIAL"
                  ? t("AI-import van eigen jaarverslagen zit niet in de proefperiode. Upgrade naar STANDARD of PREMIUM om je eigen historische data in te laden.")
                  : t("AI-import zit in het STANDARD- of PREMIUM-abonnement. Upgrade om je jaarverslag automatisch in te laden."));
                return;
              }
              setAiDepOpen(true);
            },
          }}
          menuItems={zeroBookAssets.length > 0 ? [{
            label: hideZeroBook ? t("Toon boekwaarde 0 ({n})").replace("{n}", String(zeroBookAssets.length)) : t("Verberg boekwaarde 0 ({n})").replace("{n}", String(zeroBookAssets.length)),
            icon: hideZeroBook ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />, onClick: toggleHideZero, testid: "dep-toggle-zero-book",
          }] : []}
          extraToolbar={depDuplicateCount > 0 && !club?.historicals_locked ? (
            <button
              onClick={() => setDupCleanupOpen(true)}
              data-testid="dep-cleanup-btn"
              title={t("{n} dubbele activum-naam(en) gevonden — opruimen").replace("{n}", String(depDuplicateCount))}
              className="btn-brutal bg-destructive text-white px-3 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2"
            >
              <AlertTriangle className="w-4 h-4" /> {t("Duplicaten")} ({depDuplicateCount})
            </button>
          ) : null}
        />
      )}
      {tab === "depreciations" && zeroBookAssets.length > 0 && (
        <div className="mt-2 text-xs text-muted-foreground" data-testid="dep-hidden-count">
          {hideZeroBook
            ? t("{shown} activa getoond · {hidden} verborgen met boekwaarde 0").replace("{shown}", String(depreciationsFiltered.length)).replace("{hidden}", String(zeroBookAssets.length))
            : t("{shown} activa getoond (waarvan {hidden} met boekwaarde 0)").replace("{shown}", String(depreciationsFiltered.length)).replace("{hidden}", String(zeroBookAssets.length))}
        </div>
      )}
      <AiDepExtractDialog
        clubId={clubId}
        open={aiDepOpen}
        onOpenChange={setAiDepOpen}
        onImported={() => load()}
      />
      <DuplicateCleanupDialog
        clubId={clubId}
        open={dupCleanupOpen}
        onOpenChange={setDupCleanupOpen}
        onDone={() => load()}
      />
      <RollForwardDialog
        clubId={clubId}
        open={rollOpen}
        onOpenChange={setRollOpen}
        scenarios={scenarios}
        historicals={historicals}
        budgets={budgets}
        onRolled={() => load()}
      />
    </AppLayout>
  );
}


async function downloadXlsxFrom(endpoint, mode, t, scope) {
  if (!endpoint) return;
  const url = `${API_BASE}${endpoint}`;
  const token = tokenStore.get();
  try {
    const res = await fetch(url, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      toast.error(mode === "template" ? t("Sjabloon downloaden mislukt") : t("Export mislukt"));
      return;
    }
    const blob = await res.blob();
    const filename = endpoint.split("/").pop();
    const wrote = await saveOrDownload(blob, filename, scope);
    if (wrote) toast.success(t("Opgeslagen in map ‘{name}’").replace("{name}", filename));
  } catch (e) {
    toast.error(mode === "template" ? t("Sjabloon downloaden mislukt") : t("Export mislukt"));
  }
}


function DataTable({ t, title, columns, rows, subposts, clubId, onDelete, upload, templateEndpoint, exportEndpoint, addFields, onAdd, onUpdate, onSubpostsChange, testid, extraToolbar, rowClassName, aiImport, menuItems = [], locked = false }) {
  const fileRef = useRef();
  const folderName = useDownloadFolderName(clubId);
  const uploadTip = folderName
    ? t("Zoek je bestand in de gekozen map ‘{name}’").replace("{name}", folderName)
    : t("Nog geen downloadmap voor deze club — kies er één rechtsboven in de balk om exports en imports op één plek te bundelen.");
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(() => Object.fromEntries((addFields || []).map((f) => [f.key, f.default])));
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const hasSubposts = Array.isArray(subposts) && subposts.length > 0;
  useEffect(() => {
    setDraft(Object.fromEntries((addFields || []).map((f) => [f.key, f.default])));
  }, [addFields]);
  const startEdit = (r) => {
    setEditingId(r.id);
    const base = {};
    for (const f of addFields || []) base[f.key] = r[f.key] ?? f.default;
    // Preserve nested breakdown arrays so the PUT payload keeps them intact.
    base.__breakdowns = {
      revenue_breakdown: r.revenue_breakdown || [],
      opex_breakdown: r.opex_breakdown || [],
      other_income_breakdown: r.other_income_breakdown || [],
    };
    setEditDraft(base);
  };
  const _serializeDraft = (source) => {
    const payload = {};
    for (const f of addFields || []) {
      const v = source[f.key];
      if (f.type === "num" || f.type === "int") {
        const n = Number(v);
        payload[f.key] = Number.isNaN(n) ? 0 : (f.type === "int" ? Math.round(n) : n);
      } else if (f.type === "bool") {
        payload[f.key] = Boolean(v);
      } else if (f.type === "date") {
        payload[f.key] = v ? String(v) : null;
      } else {
        payload[f.key] = String(v ?? "");
      }
    }
    return payload;
  };
  const cancelEdit = () => { setEditingId(null); setEditDraft({}); };
  const saveEdit = async () => {
    const payload = _serializeDraft(editDraft);
    if (editDraft.__breakdowns) {
      Object.assign(payload, editDraft.__breakdowns);
    }
    await onUpdate?.(editingId, payload);
    cancelEdit();
  };
  const submitDraft = async () => {
    const payload = _serializeDraft(draft);
    await onAdd?.(payload);
    setDraft(Object.fromEntries((addFields || []).map((f) => [f.key, f.default])));
    setAdding(false);
  };
  const downloadTemplate = async () => {
    return downloadXlsxFrom(templateEndpoint, "template", t, clubId);
  };
  const exportRows = async () => {
    return downloadXlsxFrom(exportEndpoint, "export", t, clubId);
  };
  return (
    <div className="bg-card border-2 border-foreground heavy-shadow">
      <div className="p-5 border-b-2 border-foreground flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display font-black text-2xl uppercase tracking-tight">{title}</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {onAdd && (
            <button onClick={() => setAdding((v) => !v)} data-testid={`${testid}-add-btn`} disabled={locked}
              className={`btn-brutal px-3 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2 disabled:opacity-40 ${adding ? "bg-foreground text-background" : "bg-primary text-primary-foreground"}`}>
              <Plus className={`w-4 h-4 transition-transform ${adding ? "rotate-45" : ""}`} /> {adding ? t("Annuleren") : t("Nieuwe rij")}
            </button>
          )}
          {extraToolbar}
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv" className="hidden"
            data-testid={`${testid}-file-input`}
            onChange={(e) => { if (e.target.files?.[0]) { upload(e.target.files[0]); e.target.value = ""; } }} />
          <MoreMenu size="sm" testid={`${testid}-more-menu`} items={[
            exportEndpoint && { label: t("Excel exporteren"), icon: <Download className="w-4 h-4" />, onClick: exportRows, testid: `${testid}-export-btn` },
            { label: t("Sjabloon downloaden"), icon: <FileSpreadsheet className="w-4 h-4" />, onClick: downloadTemplate, testid: `${testid}-template-btn` },
            ...(menuItems.length ? ["separator", ...menuItems] : []),
          ]} />
        </div>
      </div>
      {aiImport && (
        <ImportHub
          variant={rows.length === 0 ? "start" : "compact"}
          testid={`${testid}-import-hub`}
          locked={locked}
          lockedHint={t("De gegevens zijn vergrendeld — ontgrendel eerst om te importeren.")}
          ai={{ ...aiImport, onClick: () => { if (locked) { toast.error(t("De gegevens zijn vergrendeld — ontgrendel eerst om te importeren.")); return; } aiImport.onClick(); } }}
          excel={{ onTemplate: downloadTemplate, onImport: () => fileRef.current?.click(), importTitle: uploadTip, folderName, importLabel: t("Ingevulde Excel importeren") }}
          manual={onAdd ? { label: t("Handmatig een rij toevoegen"), onClick: () => setAdding(true) } : null}
        />
      )}
      {adding && onAdd && (
        <div className="p-5 border-b-2 border-foreground bg-muted/40" data-testid={`${testid}-add-form`}>
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
            {(addFields || []).map((f) => (
              <div key={f.key}>
                <label className="stat-label">{f.label}</label>
                {f.type === "bool" ? (
                  <label className="mt-1 inline-flex items-center gap-2 border-2 border-foreground bg-background px-3 py-2 w-full cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(draft[f.key])}
                      onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.checked }))}
                      data-testid={`${testid}-add-${f.key}`}
                    />
                    <span className="text-xs text-muted-foreground">{f.checkboxLabel || ""}</span>
                  </label>
                ) : (
                  <input
                    type={f.type === "text" ? "text" : f.type === "date" ? "date" : "number"}
                    step={f.type === "int" ? "1" : "any"}
                    value={draft[f.key] ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); submitDraft(); }
                      else if (e.key === "Escape") { e.preventDefault(); setAdding(false); }
                    }}
                    data-testid={`${testid}-add-${f.key}`}
                    className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-4 items-center flex-wrap">
            <button onClick={submitDraft} data-testid={`${testid}-add-save`}
              className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm">
              {t("Opslaan")}
            </button>
            <button onClick={() => setAdding(false)}
              className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold text-sm">
              {t("Annuleren")}
            </button>
            <span className="text-xs text-muted-foreground ml-2">
              <kbd className="border border-foreground/40 px-1 rounded text-[10px]">Enter</kbd> {t("opslaan")} · <kbd className="border border-foreground/40 px-1 rounded text-[10px]">Esc</kbd> {t("annuleren")}
            </span>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-foreground text-background">
              {hasSubposts && <th className="w-8"></th>}
              {columns.map(([k, l]) => (
                <th key={k} className="text-left px-4 py-2 font-display uppercase text-xs tracking-widest">{l}</th>
              ))}
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length + 1 + (hasSubposts ? 1 : 0)} className="p-6 text-center text-muted-foreground">{t("Nog geen gegevens — kies hierboven hoe je wilt beginnen.")}</td></tr>
            )}
            {rows.map((r, i) => {
              const isEditing = editingId === r.id;
              const rowBd = {
                revenue: r.revenue_breakdown || [],
                opex: r.opex_breakdown || [],
                other_income: r.other_income_breakdown || [],
              };
              const rowHasBd = hasSubposts && (rowBd.revenue.length + rowBd.opex.length + rowBd.other_income.length) > 0;
              const isExpanded = expandedId === r.id;
              return (
                <Fragment key={r.id}>
                <tr className={`${isEditing ? "bg-primary/10" : (i % 2 ? "bg-muted/40" : "")} ${rowClassName ? rowClassName(r) : ""}`}>
                  {hasSubposts && (
                    <td className="px-2 text-center">
                      <button onClick={() => setExpandedId(isExpanded ? null : r.id)}
                        data-testid={`${testid}-expand-${r.id}`}
                        title={isExpanded ? t("Details verbergen") : t("Details tonen")}
                        className="text-muted-foreground hover:text-primary font-mono-num text-xs">
                        {isExpanded ? "▾" : "▸"}
                      </button>
                    </td>
                  )}
                  {columns.map(([k]) => {
                    const spec = (addFields || []).find((f) => f.key === k);
                    if (isEditing && spec && onUpdate) {
                      return (
                        <td key={k} className="px-2 py-1">
                          {spec.type === "bool" ? (
                            <input
                              type="checkbox"
                              checked={Boolean(editDraft[k])}
                              onChange={(e) => setEditDraft((d) => ({ ...d, [k]: e.target.checked }))}
                              data-testid={`${testid}-edit-${r.id}-${k}`}
                              className="w-5 h-5 cursor-pointer"
                            />
                          ) : (
                            <input
                              type={spec.type === "text" ? "text" : spec.type === "date" ? "date" : "number"}
                              step={spec.type === "int" ? "1" : "any"}
                              value={editDraft[k] ?? ""}
                              onChange={(e) => setEditDraft((d) => ({ ...d, [k]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); saveEdit(); }
                                else if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                              }}
                              data-testid={`${testid}-edit-${r.id}-${k}`}
                              className="w-full border-2 border-foreground bg-background px-2 py-1 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                            />
                          )}
                        </td>
                      );
                    }
                    return (
                      <td key={k} className="px-4 py-2 font-mono-num">
                        {spec?.type === "bool"
                          ? (r[k] === false ? t("Nee") : t("Ja"))
                          : spec?.type === "date"
                            ? (r[k] || "—")
                            : (typeof r[k] === "number" && !["year", "acquired_year", "useful_life_years", "useful_life_months"].includes(k)
                              ? fmtCurrency(r[k])
                              : r[k] ?? "—")}
                      </td>
                    );
                  })}
                  <td className="px-2 whitespace-nowrap">
                    {isEditing ? (
                      <div className="flex gap-1">
                        <button onClick={saveEdit} data-testid={`${testid}-edit-save-${r.id}`}
                          className="text-primary hover:text-primary/80" title={t("Opslaan")}>
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={cancelEdit} data-testid={`${testid}-edit-cancel-${r.id}`}
                          className="text-muted-foreground hover:text-foreground" title={t("Annuleren")}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        {onUpdate && (
                          <button onClick={() => startEdit(r)} data-testid={`${testid}-edit-${r.id}`}
                            className="opacity-40 hover:opacity-100 hover:text-primary" title={t("Bewerken")}>
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => onDelete(r)} data-testid={`${testid}-delete-${r.id}`}
                          className="opacity-40 hover:opacity-100 hover:text-destructive" title={t("Verwijderen")}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                {hasSubposts && isExpanded && (
                  <tr key={`${r.id}-detail`} className="bg-muted/60">
                    <td></td>
                    <td colSpan={columns.length + 1} className="px-4 py-3">
                      <SubpostRowEditor
                        t={t}
                        row={r}
                        subposts={subposts}
                        clubId={clubId}
                        onSubpostsChange={onSubpostsChange}
                        onSave={(payload) => onUpdate?.(r.id, payload)}
                        testid={testid}
                      />
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


const CAT_TO_FIELD = {
  revenue: "revenue_breakdown",
  opex: "opex_breakdown",
  other_income: "other_income_breakdown",
};
const CAT_LABELS_NL = {
  revenue: "Omzet",
  opex: "Bedr. kosten",
  other_income: "Overige inkomsten",
};

function findSubpostAmount(row, cat, name) {
  const arr = row[CAT_TO_FIELD[cat]] || [];
  const hit = arr.find((x) => x.name === name);
  return hit ? Number(hit.amount) || 0 : 0;
}

function SubpostRowEditor({ t, row, subposts, clubId, onSubpostsChange, onSave, testid }) {
  const grouped = useMemo(() => {
    const g = { revenue: [], opex: [], other_income: [] };
    for (const s of subposts || []) g[s.category]?.push(s);
    return g;
  }, [subposts]);

  const totals = useMemo(() => {
    const sum = (arr) => arr.reduce((a, b) => a + (Number(b.amount) || 0), 0);
    return {
      revenue: sum(row.revenue_breakdown || []),
      opex: sum(row.opex_breakdown || []),
      other_income: sum(row.other_income_breakdown || []),
    };
  }, [row]);

  const [adding, setAdding] = useState(null); // category currently in "add new subpost" mode
  const [newName, setNewName] = useState("");

  const startAdd = (cat) => { setAdding(cat); setNewName(""); };
  const cancelAdd = () => { setAdding(null); setNewName(""); };
  const submitAdd = async (cat) => {
    const name = (newName || "").trim();
    if (!name) { cancelAdd(); return; }
    try {
      await api.post(`/clubs/${clubId}/subposts`, { category: cat, name });
      toast.success(t("Subpost toegevoegd"));
      cancelAdd();
      onSubpostsChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveAmount = async (cat, name, rawVal) => {
    const num = Number(rawVal);
    const amount = Number.isFinite(num) ? num : 0;
    const bdField = CAT_TO_FIELD[cat];
    const current = row[bdField] || [];
    const list = current.slice();
    const idx = list.findIndex((x) => x.name === name);
    if (idx >= 0) list[idx] = { name, amount };
    else if (amount !== 0) list.push({ name, amount });
    else return; // do not create a zero entry the user never touched
    const payload = {
      year: row.year,
      revenue: row.revenue || 0,
      operating_costs: row.operating_costs || 0,
      depreciation: row.depreciation || 0,
      other_income: row.other_income || 0,
      balance: row.balance || 0,
      cash_bank_balance: row.cash_bank_balance || 0,
      revenue_breakdown: row.revenue_breakdown || [],
      opex_breakdown: row.opex_breakdown || [],
      other_income_breakdown: row.other_income_breakdown || [],
      [bdField]: list,
    };
    await onSave?.(payload);
  };

  return (
    <div className="grid md:grid-cols-3 gap-4 text-xs" data-testid={`${testid}-subposts-editor-${row.id}`}>
      {["revenue", "opex", "other_income"].map((cat) => (
        <div key={cat} className="border-2 border-foreground/20 p-3">
          <div className="flex items-baseline justify-between mb-2">
            <div className="stat-label">{t(CAT_LABELS_NL[cat])}</div>
            <div className="font-mono-num text-[11px] text-muted-foreground">
              {t("Som")}: <span className="text-foreground">{fmtCurrency(totals[cat])}</span>
            </div>
          </div>
          {grouped[cat].length === 0 && adding !== cat && (
            <div className="text-muted-foreground italic mb-2">
              {t("Nog geen subposten in deze categorie.")}
            </div>
          )}
          {grouped[cat].map((sp) => (
            <SubpostAmountInput
              key={`${row.id}-${cat}-${sp.name}`}
              name={sp.name}
              initial={findSubpostAmount(row, cat, sp.name)}
              onCommit={(v) => saveAmount(cat, sp.name, v)}
              testid={`${testid}-sp-input-${row.id}-${cat}-${sp.name}`}
            />
          ))}
          {adding === cat ? (
            <div className="flex items-center gap-1 mt-2 pt-2 border-t border-foreground/10">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); submitAdd(cat); }
                  else if (e.key === "Escape") { e.preventDefault(); cancelAdd(); }
                }}
                placeholder={t("Naam nieuwe subpost")}
                autoFocus
                data-testid={`${testid}-sp-newname-${row.id}-${cat}`}
                className="flex-1 border-2 border-foreground bg-background px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button onClick={() => submitAdd(cat)} data-testid={`${testid}-sp-newsave-${row.id}-${cat}`}
                className="text-primary hover:text-primary/80" title={t("Opslaan")}>
                <Check className="w-4 h-4" />
              </button>
              <button onClick={cancelAdd} className="text-muted-foreground" title={t("Annuleren")}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => startAdd(cat)}
              data-testid={`${testid}-sp-addbtn-${row.id}-${cat}`}
              className="mt-2 pt-2 border-t border-foreground/10 w-full text-left inline-flex items-center gap-1 text-primary hover:text-primary/80 font-display uppercase tracking-widest text-[11px] font-bold"
            >
              <Plus className="w-3 h-3" /> {t("Subpost toevoegen")}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function SubpostAmountInput({ name, initial, onCommit, testid }) {
  const [val, setVal] = useState(initial === 0 ? "" : String(initial));
  // Sync when parent row updates (e.g. after saving a different amount).
  useEffect(() => {
    setVal(initial === 0 ? "" : String(initial));
  }, [initial]);
  const commit = () => {
    const rawNum = Number(val);
    const currentNum = Number.isFinite(rawNum) ? rawNum : 0;
    if (currentNum === (initial || 0)) return;
    onCommit(currentNum);
  };
  return (
    <div className="flex items-center gap-2 py-1 border-b border-dashed border-foreground/10 last:border-b-0">
      <span className="flex-1 truncate">{name}</span>
      <input
        type="number"
        step="any"
        value={val}
        placeholder="0"
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
          else if (e.key === "Escape") { e.preventDefault(); setVal(initial === 0 ? "" : String(initial)); e.currentTarget.blur(); }
        }}
        onBlur={commit}
        data-testid={testid}
        className="w-28 border-2 border-foreground/40 bg-background px-2 py-1 font-mono-num text-right focus:outline-none focus:ring-2 focus:ring-primary"
      />
    </div>
  );
}
